import React, { useState, useEffect, useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import PrivateRoute from './components/PrivateRoute';

import TopBar from './components/TopBar';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Public pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ChangePassword from './pages/ChangePassword';
import ResetPassword from './pages/ResetPassword';

// Admin
import AdminUsers from './pages/AdminUsers';
import AdminMonitoring from './pages/AdminMonitoring';

import Bugs from './pages/Bugs';
import BugDetail from './pages/BugDetail';
import CompletedBugs from './pages/CompletedBugs';
import MySupportTickets from './pages/MySupportTickets';
import ITSupport from './pages/ITSupport';
import ContactITSupport from './pages/ContactITSupport';

// Core
import Home from './pages/Home';
import OrgStructure from './pages/OrgStructure';
import Workflows from './pages/Workflows';

// HR Management
import HRManagement from './pages/HRManagement';

// Inventory Management
import InventoryManagement from './pages/InventoryManagement';

// Search
import SearchResults from './pages/SearchResults';

// OKRs
import PersonalOKRs from './pages/PersonalOKRs';
import TeamOKRs from './pages/TeamOKRs';
import MyOKRs from './pages/MyOKRs';
import MyAnnualOKRs from './pages/MyAnnualOKRs';
import People from './pages/People';
import Teams from './pages/Teams';
import Departments from './pages/Departments';

// Utilities
import VisualMap from './pages/VisualMap';
import SafetyProcedures from './pages/SafetyProcedures';
import SLAs from './pages/SLAs';
import Chat from './pages/Chat';

// Cost Cards & Templates
import CostCards from './pages/CostCard';
import TemplatePicker from './pages/TemplatePicker';
import CreateCostCard from './pages/CreateCostCard';
import EditCostCard from './pages/EditCostCard';
import CreateCustomTemplate from './pages/CreateCustomTemplate';
import EditCustomTemplate from './pages/EditCustomTemplate';

// Contacts
import ContactsPage from './pages/ContactsPage';

// Inventory Management

// OKR CRUD
import CreateOKR from './pages/CreateOKR';
import EditOKR from './pages/EditOKR';

// Workflows
import TechnicalSalesWorkflow from './pages/TechnicalSalesWorkflow';
import StoreManagementWorkflow from './pages/StoreManagementWorkflow';
import SalesWorkflow from './pages/SalesWorkflow';
import ImportWorkflow from './pages/ImportWorkflow';
import WorkflowAutomations from './pages/WorkflowAutomations';
import StoreManagementWorkflowAutomation from './components/StoreManagementWorkflow';

// Job Descriptions
import HeadOfSupplyChain from './pages/jobDescriptions/HeadOfSupplyChain';
import OfficeAdministrator from './pages/jobDescriptions/OfficeAdministrator';
import TechnicalOfficer from './pages/jobDescriptions/TechnicalOfficer';
import ManagingDirector from './pages/jobDescriptions/ManagingDirector';
import FinanceManager from './pages/jobDescriptions/FinanceManager';
import Accountant from './pages/jobDescriptions/Accountant';
import BusinessDevelopmentManager from './pages/jobDescriptions/BusinessDevelopmentManager';

// Interviews
import EmployeesReviews from './pages/EmployeesReviews';
import AccountantInterview from './pages/interviewProcesses/AccountantInterview';
import HeadOfSupplyChainInterview from './pages/interviewProcesses/HeadOfSupplyChainInterview';
import OfficeAdministratorInterview from './pages/interviewProcesses/OfficeAdministratorInterview';
import TechnicalOfficerInterview from './pages/interviewProcesses/TechnicalOfficerInterview';
import ManagingDirectorInterview from './pages/interviewProcesses/ManagingDirectorInterview';
import BusinessDevelopmentManagerInterview from './pages/interviewProcesses/BusinessDevelopmentManagerInterview';

// Company Strategy
import CompanyStrategy from './pages/CompanyStrategy/CompanyStrategy';
import BusinessContinuityPlan from './pages/CompanyStrategy/BusinessContinuityPlan';
import DataManagementSOP from './pages/CompanyStrategy/DataManagementSOP';
import ProcurementSOP from './pages/CompanyStrategy/ProcurementSOP';
import RiskComplianceSOP from './pages/CompanyStrategy/RiskComplianceSOP';

import './App.css';
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopButton from './components/ScrollToTopButton';
import Settings from './pages/Settings';
import ActivityDashboard from './pages/ActivityDashboard';

// Simple mobile detection hook
function useMobileDetection() {
  const [isMobile, setIsMobile] = useState(true); // Start with mobile assumption

  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      const mobile = width <= 768;
      console.log('Mobile check:', { width, mobile });
      return mobile;
    };

    // Check mobile after a brief delay
    const timeoutId = setTimeout(() => {
      setIsMobile(checkMobile());
    }, 100);

    const handleResize = () => {
      setIsMobile(checkMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return { isMobile };
}

function AppContent() {
  const { user } = useContext(AuthContext);
  const { isMobile } = useMobileDetection();
  const isAdminOrSuper = ['admin', 'super'].includes(user?.role);
  
  console.log('AppContent render:', { 
    isMobile, 
    user: !!user, 
    userRole: user?.role,
    userName: user?.name,
    windowWidth: window.innerWidth,
    currentPath: window.location.pathname
  });

  console.log('About to render main content:', { user: !!user, isMobile });

  return (
    <>
      <ScrollToTop />
      {user && (
        <>
          {!isMobile && <TopBar />}
          <Navbar />
        </>
      )}

      <div className="content-area">
        <Routes>
          {/* Public routes - accessible without authentication */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Protected routes - require authentication */}
          {user ? (
            <>
              {/* Core */}
              <Route path="/" element={<Home />} />
              <Route path="/org-structure" element={<OrgStructure />} />
              <Route path="/workflows" element={<Workflows />} />
              <Route path="/activity" element={<ActivityDashboard />} />
              <Route path="/change-password" element={<ChangePassword />} />

              {/* Admin-only */}
              <Route
                path="/admin/users"
                element={
                  isAdminOrSuper
                    ? <AdminUsers />
                    : <Navigate to="/" replace />
                }
              />
              <Route
                path="/admin/monitoring"
                element={
                  isAdminOrSuper
                    ? <AdminMonitoring />
                    : <Navigate to="/" replace />
                }
              />
              <Route
                path="/admin/bugs"
                element={
                  user?.role === 'super'
                    ? <Bugs />
                    : <Navigate to="/" replace />
                }
              />
              <Route
                path="/admin/bugs/:id"
                element={
                  user?.role === 'super'
                    ? <BugDetail />
                    : <Navigate to="/" replace />
                }
              />
              <Route
                path="/admin/completed-bugs"
                element={
                  user?.role === 'super'
                    ? <CompletedBugs />
                    : <Navigate to="/" replace />
                }
              />

              {/* Search */}
              <Route path="/search" element={<SearchResults />} />

              {/* OKR pages */}
              <Route path="/company-okrs/my-okrs" element={<PersonalOKRs />} />
              <Route path="/company-okrs/my-team" element={<TeamOKRs />} />
              <Route path="/company-okrs/quarter" element={<MyOKRs />} />
              {user?.role === 'standard' && (
                <Route path="/company-okrs" element={<Navigate to="/company-okrs/quarter" replace />} />
              )}
              {isAdminOrSuper && (
                <>
                  <Route path="/company-okrs/annual" element={<MyAnnualOKRs />} />
                  <Route path="/company-okrs/people" element={<People />} />
                  <Route path="/company-okrs/teams" element={<Teams />} />
                  <Route path="/company-okrs/departments" element={<Departments />} />
                </>
              )}

              {/* Utilities */}
              <Route path="/visual-map" element={<VisualMap />} />
              <Route path="/safety-procedures" element={<SafetyProcedures />} />
              <Route path="/slas" element={<SLAs />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/chat/join/:roomId" element={<Chat />} />
              <Route path="/my-support-tickets" element={<MySupportTickets />} />
              <Route path="/it-support" element={<ITSupport />} />
              <Route path="/contact-it-support" element={<ContactITSupport />} />

              {/* Templates */}
              <Route path="/templates" element={<Navigate to="/costcards/new" replace />} />
              <Route path="/templates/new" element={<CreateCustomTemplate />} />
              <Route path="/templates/edit/:id" element={<EditCustomTemplate />} />

              {/* Cost Cards */}
              <Route path="/costcards" element={<CostCards />} />
              <Route path="/costcards/new" element={<TemplatePicker />} />
              <Route path="/costcards/create/:templateKey" element={<CreateCostCard />} />

              {/* Inventory Management - moved to line 316 */}

              {/* OKR CRUD */}
              <Route
                path="/createokr"
                element={isAdminOrSuper ? <CreateOKR /> : <Navigate to="/" replace />}
              />
              <Route
                path="/editokr/:id"
                element={isAdminOrSuper ? <EditOKR /> : <Navigate to="/" replace />}
              />

              {/* Workflows */}
              <Route path="/workflows/technical-sales" element={<TechnicalSalesWorkflow />} />
              <Route path="/workflows/store-management" element={<StoreManagementWorkflow />} />
              <Route path="/workflows/sales" element={<SalesWorkflow />} />
              <Route path="/workflows/import" element={<ImportWorkflow />} />
              
              {/* Workflow Automations */}
              <Route path="/intranet/automation/workflows" element={<WorkflowAutomations />} />
              <Route path="/intranet/automation/store-management" element={<StoreManagementWorkflowAutomation />} />

              {/* Job Descriptions */}
              <Route path="/human-resources/job-descriptions/head-of-supply-chain" element={<HeadOfSupplyChain />} />
              <Route path="/human-resources/job-descriptions/office-administrator" element={<OfficeAdministrator />} />
              <Route path="/human-resources/job-descriptions/technical-officer" element={<TechnicalOfficer />} />
              <Route path="/human-resources/job-descriptions/managing-director" element={<ManagingDirector />} />
              <Route path="/human-resources/job-descriptions/finance-manager" element={<FinanceManager />} />
              <Route path="/human-resources/job-descriptions/accountant" element={<Accountant />} />
              <Route path="/human-resources/job-descriptions/business-development-manager" element={<BusinessDevelopmentManager />} />

              {/* Interviews */}
              <Route path="/human-resources/employees-reviews" element={<EmployeesReviews />} />
              <Route path="/human-resources/interview-processes/accountant" element={<AccountantInterview />} />
              <Route path="/human-resources/interview-processes/head-of-supply-chain" element={<HeadOfSupplyChainInterview />} />
              <Route path="/human-resources/interview-processes/office-administrator" element={<OfficeAdministratorInterview />} />
              <Route path="/human-resources/interview-processes/technical-officer" element={<TechnicalOfficerInterview />} />
              <Route path="/human-resources/interview-processes/managing-director" element={<ManagingDirectorInterview />} />
              <Route path="/interview-processes/business-development-manager" element={<BusinessDevelopmentManagerInterview />} />

              {/* Company Strategy */}
              <Route path="/company-strategy/company-strategy" element={<CompanyStrategy />} />
              <Route path="/company-strategy/nesim-business-continuity-plan-final" element={<BusinessContinuityPlan />} />
              <Route path="/company-strategy/nesim-data-management-sop" element={<DataManagementSOP />} />
              <Route path="/company-strategy/nesim-procurement-sop-final" element={<ProcurementSOP />} />
              <Route path="/company-strategy/nesim-risk-and-compliance-sops-final" element={<RiskComplianceSOP />} />

              {/* HR Management */}
              <Route path="/hr" element={<HRManagement />} />
              <Route path="/inventory" element={<InventoryManagement />} />
              
              {/* Contacts */}
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/contacts/suppliers" element={<ContactsPage />} />
              <Route path="/contacts/customers" element={<ContactsPage />} />

              {/* Settings */}
              <Route path="/settings" element={<Settings />} />

              {/* Chat routes already above */}

              {/* Catch-all for authenticated users */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            // If not authenticated, redirect to login and allow access to public routes
            <>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          )}
        </Routes>
      </div>
      <ScrollToTopButton />
    </>
  );
}

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error Boundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f8fafc',
          fontFamily: 'Poppins, sans-serif',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            maxWidth: '500px',
            width: '100%'
          }}>
            <h1 style={{ color: '#dc2626', marginBottom: '16px', fontSize: '24px' }}>
              Something went wrong
            </h1>
            <p style={{ color: '#64748b', marginBottom: '20px' }}>
              The app encountered an error. Please refresh the page to try again.
            </p>
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '12px',
              maxHeight: '200px',
              overflow: 'auto',
              marginBottom: '16px'
            }}>
              <div style={{ marginBottom: '8px', wordBreak: 'break-word' }}>
                <strong>Error:</strong> {String(this.state.error && (this.state.error.message || this.state.error))}
              </div>
              {this.state.errorInfo?.componentStack && (
                <div>
                  <strong>Stack:</strong>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{this.state.errorInfo.componentStack.trim().split('\n').slice(0,6).join('\n')}</pre>
                </div>
              )}
            </div>
            <button 
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function App() {
  console.log('App component rendering');
  
  return (
    <ErrorBoundary>
      <ChatProvider>
        <AppContent />
      </ChatProvider>
    </ErrorBoundary>
  );
}